import { useRef, useState } from "react"
import type { ToolUIPart } from "ai"
import { AlertCircle, Check, Loader2, X } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { CoachMarkdown } from "@/components/coach-markdown"
import { Progress } from "@/components/ui/progress"
import { PruefungsabschlussCard } from "@/components/pruefungsabschluss-card"
import { Textarea } from "@/components/ui/textarea"
import { useCoachThreadApi } from "@/lib/coach-thread-api"
import { usePruefungChat } from "@/lib/pruefung-chat"
import { cn } from "@/lib/utils"
import {
  PRUEFUNGS_FRAGE_TYP_LABELS,
  type PruefungsAbschluss,
  type PruefungsAuswertung,
  type PruefungsFrageKarte,
} from "@nl/shared"

/**
 * Eine Prüfungsfrage als beantwortbare Karte im Gespräch.
 *
 * Die Karte ist der Ort, an dem geantwortet wird — nicht das Eingabefeld. Das
 * ist kein Stilmittel: nur so steht fest, welche Frage beantwortet wurde und
 * welche Option gemeint war, und nur so kann der Server rechnerisch bewerten
 * statt ein Modell raten zu lassen.
 *
 * Der Ablauf danach ist zweistufig, und die Reihenfolge ist wichtig: erst die
 * Auswertung (sofort sichtbar, kein Warten auf das Modell), dann ein
 * Gesprächszug, damit der Coach darauf eingeht.
 */
export function PruefungsfrageCard({ part }: { part: ToolUIPart }) {
  // A stored part arrives as output-available straight away; only one that
  // was still running while mounted is a finish happening right now.
  const frisch = useRef(part.state !== "output-available")

  if (part.state === "input-streaming" || part.state === "input-available") {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Frage wird geholt …
      </div>
    )
  }

  if (part.state === "output-error") {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <AlertCircle className="h-3.5 w-3.5" />
        Die Frage konnte nicht geladen werden.
      </div>
    )
  }

  const output = part.output as
    | PruefungsFrageKarte
    | { keineFrage: true; grund: string; abschluss?: PruefungsAbschluss }
    | undefined

  if (output && "keineFrage" in output && output.abschluss) {
    return (
      <PruefungsabschlussCard
        abschluss={output.abschluss}
        feiern={frisch.current}
      />
    )
  }

  // Kein Katalogtreffer heißt: der Coach sagt es im Text. Eine Karte mit einer
  // Begründung darin wäre dieselbe Auskunft zweimal.
  if (!output || "keineFrage" in output) return null

  return <Frage karte={output} toolCallId={part.toolCallId} />
}

function Frage({
  karte,
  toolCallId,
}: {
  karte: PruefungsFrageKarte
  toolCallId: string
}) {
  const chat = usePruefungChat()
  const thread = useCoachThreadApi()
  const [gewaehlt, setGewaehlt] = useState<string[]>([])
  const [text, setText] = useState("")
  const [laeuft, setLaeuft] = useState(false)

  const auswertung = chat?.auswertungFuer(toolCallId)
  const beantwortet = !!auswertung
  const mehrfach = karte.typ === "multi"
  const offen = karte.typ === "offen"

  // Bound to the thread's own gate: `ask` drops a turn silently while the
  // coach is still streaming, and a dropped turn is an answer the coach never
  // reacts to.
  const kannAbsenden =
    !!chat &&
    !laeuft &&
    thread?.canSend !== false &&
    (offen ? text.trim().length > 0 : gewaehlt.length > 0)

  function waehle(key: string) {
    if (beantwortet) return
    setGewaehlt((prev) =>
      mehrfach
        ? prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key]
        : [key]
    )
  }

  async function absenden() {
    if (!chat || !kannAbsenden) return
    setLaeuft(true)
    try {
      const ergebnis = await chat.antworten({
        frageId: karte.frageId,
        toolCallId,
        gewaehlt: offen ? [] : gewaehlt,
        text: offen ? text.trim() : undefined,
      })
      // Der Zug ist eine normale Nachricht: so steht im Verlauf lesbar, was sie
      // geantwortet hat. Die Auswertung selbst liest der Server aus dem
      // Gespräch und hängt sie wörtlich an den Systemprompt. Der Coach
      // bewertet nicht, er liest.
      thread?.ask(antwortSatz(karte, ergebnis, text))
    } catch {
      toast.error("Die Antwort konnte nicht gespeichert werden.")
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="rounded-xl border border-[var(--rule)] bg-card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <div className="eyebrow min-w-0 truncate text-muted-foreground">
          {karte.lernfeldTitel} · {PRUEFUNGS_FRAGE_TYP_LABELS[karte.typ]}
        </div>
        {karte.nummer && karte.gesamt && (
          <div className="tnum shrink-0 text-[12px] text-muted-foreground">
            Runde · Frage{" "}
            <span className="font-medium text-foreground">{karte.nummer}</span>{" "}
            von {karte.gesamt}
          </div>
        )}
      </div>
      {karte.nummer && karte.gesamt && (
        <Fortschritt
          nummer={karte.nummer}
          gesamt={karte.gesamt}
          beantwortet={beantwortet}
        />
      )}
      {/* Markdown statt reinem Text, weil eine Frage eine Tabelle tragen kann
          (S1-03 rechnet auf den Entgeltpunkten). Der Fragetext der übrigen
          Fragen ist Prosa und sieht unverändert aus — keine Zeile beginnt mit
          einem Listen- oder Überschriftenzeichen. */}
      <div className="mt-2.5 font-medium">
        <CoachMarkdown text={karte.stamm} />
      </div>

      {offen ? (
        <Textarea
          value={beantwortet ? (auswertung.text ?? text) : text}
          onChange={(e) => setText(e.target.value)}
          readOnly={beantwortet}
          rows={4}
          placeholder="Deine Antwort in eigenen Worten …"
          className="mt-3 text-[14px]"
          aria-label="Deine Antwort"
        />
      ) : (
        <div
          className="mt-3 space-y-1.5"
          role={mehrfach ? "group" : "radiogroup"}
          aria-label="Antwortmöglichkeiten"
        >
          {karte.optionen.map((option) => (
            <Option
              key={option.key}
              option={option}
              mehrfach={mehrfach}
              gewaehlt={
                beantwortet
                  ? auswertung.gewaehlt.includes(option.key)
                  : gewaehlt.includes(option.key)
              }
              richtig={
                beantwortet
                  ? auswertung.richtigeOptionen.includes(option.key)
                  : undefined
              }
              onClick={() => waehle(option.key)}
              disabled={beantwortet}
            />
          ))}
        </div>
      )}

      {beantwortet ? (
        <Ergebnis auswertung={auswertung} />
      ) : (
        <div className="mt-3 flex items-center gap-3">
          <Button
            type="button"
            size="sm"
            onClick={() => void absenden()}
            disabled={!kannAbsenden}
          >
            {laeuft && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Antwort abgeben
          </Button>
          {mehrfach && (
            <span className="text-[12.5px] text-muted-foreground">
              Mehrere Antworten möglich.
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Counts answered questions, not the one on screen: the bar moves when the
 * answer is in, which is the moment something was actually done.
 *
 * Thin and grey on purpose. This is the current thread only, while the
 * Lernstand card counts across all threads; in the same accent colour the two
 * bars read as one measure that disagrees with itself.
 */
function Fortschritt({
  nummer,
  gesamt,
  beantwortet,
}: {
  nummer: number
  gesamt: number
  beantwortet: boolean
}) {
  const erledigt = beantwortet ? nummer : nummer - 1
  return (
    <Progress
      value={Math.round((erledigt / gesamt) * 100)}
      aria-label={`${erledigt} von ${gesamt} Fragen dieser Runde beantwortet`}
      className="mt-2 h-0.5 [&>[data-slot=progress-indicator]]:bg-muted-foreground/60"
    />
  )
}

function Option({
  option,
  mehrfach,
  gewaehlt,
  richtig,
  onClick,
  disabled,
}: {
  option: { key: string; text: string }
  mehrfach: boolean
  gewaehlt: boolean
  /** Undefiniert, solange nicht bewertet wurde — dann ist nichts grün oder rot. */
  richtig?: boolean
  onClick: () => void
  disabled: boolean
}) {
  const bewertet = richtig !== undefined
  // Nach der Bewertung zählt die Lösung, nicht die Wahl: die richtige Option
  // ist immer grün, auch wenn sie übersehen wurde. Rot ist nur, was gewählt
  // und falsch war — eine nicht gewählte falsche Option ist keine Aussage
  // über sie, sondern nur eine Ablenkung, die ihre Arbeit getan hat.
  const falschGewaehlt = bewertet && gewaehlt && !richtig

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      role={mehrfach ? "checkbox" : "radio"}
      aria-checked={gewaehlt}
      className={cn(
        "flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left text-[14px] leading-snug transition-colors",
        "border-[var(--rule)] hover:bg-muted/40 disabled:hover:bg-transparent",
        gewaehlt && !bewertet && "border-primary/50 bg-primary/[0.06]",
        bewertet && richtig && "border-emerald-600/40 bg-emerald-600/[0.08]",
        falschGewaehlt && "border-destructive/40 bg-destructive/[0.08]"
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border",
          mehrfach ? "rounded-[4px]" : "rounded-full",
          gewaehlt || (bewertet && richtig)
            ? "border-transparent"
            : "border-muted-foreground/40",
          bewertet && richtig && "bg-emerald-600 text-white",
          falschGewaehlt && "bg-destructive text-white",
          gewaehlt && !bewertet && "bg-primary text-primary-foreground"
        )}
      >
        {bewertet && richtig && <Check className="h-3 w-3" />}
        {falschGewaehlt && <X className="h-3 w-3" />}
        {gewaehlt && !bewertet && <Check className="h-3 w-3" />}
      </span>
      <span>{option.text}</span>
    </button>
  )
}

function Ergebnis({ auswertung }: { auswertung: PruefungsAuswertung }) {
  return (
    <div className="mt-3 border-t border-[var(--rule)] pt-3">
      <div
        className={cn(
          "flex items-center gap-1.5 text-[13px] font-medium",
          auswertung.richtig ? "text-emerald-700" : "text-destructive"
        )}
      >
        {auswertung.richtig ? (
          <Check className="h-4 w-4" />
        ) : (
          <X className="h-4 w-4" />
        )}
        {auswertung.richtig ? "Richtig" : "Falsch"}
      </div>

      {auswertung.bewertung && (
        <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
          {auswertung.bewertung}
        </p>
      )}

      {auswertung.musterantwort && (
        <div className="mt-2">
          <div className="eyebrow text-muted-foreground">Musterantwort</div>
          <p className="mt-1 text-[13.5px] leading-relaxed whitespace-pre-line">
            {auswertung.musterantwort}
          </p>
        </div>
      )}

      {auswertung.begruendung && (
        <p className="mt-2 text-[13.5px] leading-relaxed">
          {auswertung.begruendung}
        </p>
      )}
    </div>
  )
}

/**
 * Was als Nachricht im Verlauf steht.
 *
 * Der Text des Zuges, nicht die Bewertung: das Ergebnis liest der Server aus
 * dem gespeicherten Datensatz des Gesprächs. Stünde es hier im Klartext, wäre
 * es eine Behauptung des Browsers, die ein Nutzer ändern könnte.
 */
export function antwortSatz(
  karte: PruefungsFrageKarte,
  auswertung: PruefungsAuswertung,
  text: string
): string {
  if (karte.typ === "offen") return text.trim()

  const gewaehlt = auswertung.gewaehlt
    .map((key) => karte.optionen.find((o) => o.key === key)?.text ?? key)
    .join(" / ")
  return `Meine Antwort: ${gewaehlt || "(keine)"}`
}
